import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import FileDownload from "@mui/icons-material/FileDownload"
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import RemoveModeratorIcon from "@mui/icons-material/RemoveModerator"
import {
	GridActionsCellItem,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowId,
	GridSortModel,
} from "@mui/x-data-grid"
import { useContext, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import User from "../../../api/models/user"
import userService from "../../../api/services/userService"
import { exportItem } from "../../../utils/exportUtil"
import UserContext from "../../../contexts/user"
import Role from "../../../enums/Role"
import { PAGE, SIZE } from "./constant"

function useUserAdminPage() {
	const { userRoles } = useContext(UserContext)
	const isFullAdmin = userRoles.includes(Role.ADMIN)
	const [searchInput, setSearchInput] = useState<string>("")
	const [search, setSearch] = useState<string>("")
	const [pageSize, setPageSize] = useState<number>(SIZE)
	const [formattedUsers, setFormattedUsers] = useState<any>([])
	const [exportUser, setExportUser] = useState<number>(0)
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const [pageNumber, setPageNumber] = useState<number>(PAGE)
	const [sortModel, setSortModel] = useState<GridSortModel>()
	const [sorting, setSorting] = useState<{ field: string; type?: string }>()

	const columns: GridColDef[] = [
		{
			field: "activated",
			headerName: "Activé ",
			type: "boolean",
			headerClassName: "info-cell",
			flex: 0.1,
		},
		{
			field: "civility",
			headerName: "Civilité",
			type: "string",
			flex: 0.1,
		},
		{
			field: "name",
			headerName: "Nom",
			type: "string",

			flex: 0.2,
		},
		{
			field: "firstName",
			headerName: "Prénom",
			type: "string",
			flex: 0.1,
		},

		{
			field: "email",
			headerName: "Email",
			type: "string",
			flex: 0.2,
		},
		{
			field: "internStatus",
			headerName: "Statut",
			type: "string",
			flex: 0.1,
		},
		{
			field: "roles",
			headerName: "Roles",
			renderCell: (params: GridRenderCellParams<any, User>) => {
				if (params.value) {
					if (
						Array.isArray(params.value.roles) &&
						params.value.roles.length > 0
					) {
						return params.value.roles[0]
					}
					return "Aucun rôle"
				}
				return "Aucun rôle"
			},
			type: "string",
			flex: 0.1,
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			cellClassName: "actions",
			flex: 0.2,
			// eslint-disable-next-line arrow-body-style
			getActions: user => {
				const roles: Array<{ role?: string }> = user.row.roles || []
				const isTargetAdmin = roles.some(r => r.role === Role.ADMIN)
				const isTargetSousAdmin = roles.some(
					r => r.role === Role.SOUS_ADMIN,
				)
				const actions = [
					<GridActionsCellItem
						icon={<FileDownload color="primary" />}
						label="Exporter"
						onClick={() => setExportUser(Number(user.id))}
						title="Exporter"
					/>,
					<GridActionsCellItem
						icon={<EditIcon color="secondary" />}
						label="Modifier"
						onClick={() => navigate(`/user-details/${user.id}`)}
						title="Modifier"
					/>,
					user.row.activated ? (
						<GridActionsCellItem
							icon={<RadioButtonCheckedIcon color="warning" />}
							title="Désactiver"
							label="Désactiver"
							onClick={() => handleDeactivateClick(user.id)}
						/>
					) : (
						<GridActionsCellItem
							icon={
								<RadioButtonUncheckedIcon className="uncheck" />
							}
							title="Activer"
							label="Activer"
							onClick={() =>
								postActive.mutate({
									userId: +user.id,
									activated: true,
								})
							}
						/>
					),

					<GridActionsCellItem
						icon={<DeleteIcon color="error" />}
						label="Supprimer"
						title="Supprimer"
						onClick={() => handleDeleteClick(user.id)}
					/>,
				]

				if (isFullAdmin && !isTargetAdmin) {
					actions.push(
						isTargetSousAdmin ? (
							<GridActionsCellItem
								icon={<RemoveModeratorIcon color="warning" />}
								label="Retirer sous-admin"
								title="Retirer les droits sous-admin"
								onClick={() =>
									handleSousAdminClick(user.id, false)
								}
							/>
						) : (
							<GridActionsCellItem
								icon={
									<AdminPanelSettingsIcon color="secondary" />
								}
								label="Rendre sous-admin"
								title="Donner les droits sous-admin"
								onClick={() =>
									handleSousAdminClick(user.id, true)
								}
							/>
						),
					)
				}

				return actions
			},
		},
	]

	useEffect(() => {
		if (sortModel && sortModel.length > 0) {
			setSorting({
				field: sortModel[0].field,
				type: sortModel[0].sort?.toUpperCase(),
			})
		} else {
			setSorting(undefined)
		}
	}, [sortModel])

	// debounce the raw input before it triggers a search request
	useEffect(() => {
		const handler = setTimeout(() => setSearch(searchInput), 300)
		return () => clearTimeout(handler)
	}, [searchInput])

	// a shorter filtered result set can make the grid's current page invalid
	useEffect(() => {
		setPageNumber(PAGE)
	}, [search])

	const users = useQuery(
		["users", pageNumber, search, pageSize, sorting],
		() =>
			userService
				.getAllPaginated({
					page: pageNumber,
					size: pageSize,
					query: search !== "" ? search : null,
					sortField: sorting?.field,
					sortType: sorting?.type,
				})
				.then(res => {
					res.content.forEach((c: any) => {
						c.internStatus = c?.internStatus?.name
					})
					setFormattedUsers(res.content)
					return res
				}),
		{
			keepPreviousData: true,
		},
	)

	useQuery(["rgpd-user", exportUser], () => userService.getById(exportUser), {
		enabled: exportUser > 0,
		onSuccess: (data: User) => {
			exportItem(data, `export_${data.email}_${Date.now()}`)
		},
	})

	const postActive = useMutation(
		({ activated, userId }: any) => userService.active(activated, userId),
		{
			onSuccess: (data, variables) => {
				let text: string = "L'utilisateur a été activé !"
				let icon: any = "success"
				if (variables.activated === false) {
					text = "L'utilisateur a été désactivé !"
					icon = "warning"
				}
				Swal.fire({
					position: "bottom-end",
					title: "",
					text,
					icon,
					timer: 1500,
				})

				const lOld = [...formattedUsers]

				lOld.forEach((c: User) => {
					if (c.id === variables.userId) {
						c.activated = variables.activated
					}
				})
				setFormattedUsers(lOld)
				queryClient.setQueryData(
					["users", pageNumber, search, pageSize],
					(old: any) => {
						const oldOne = Object.assign(old)

						oldOne.content = lOld
						return lOld
					},
				)
			},
		},
	)

	const postSousAdmin = useMutation(
		({ userId, sousAdmin }: { userId: number; sousAdmin: boolean }) =>
			userService.updateSousAdminRole(userId, sousAdmin),
		{
			onSuccess: (_data, variables) => {
				Swal.fire({
					position: "bottom-end",
					title: "",
					text: variables.sousAdmin
						? "Les droits sous-admin ont été accordés !"
						: "Les droits sous-admin ont été retirés !",
					icon: "success",
					timer: 1500,
				})
				users.refetch()
			},
			onError: () => {
				Swal.fire({
					position: "bottom-end",
					title: "Erreur lors de la mise à jour des droits.",
					icon: "error",
					showConfirmButton: false,
					timer: 1500,
				})
			},
		},
	)

	const deleteUser = useMutation((id: string) => userService.delete(id), {
		onSuccess: () => {
			users.refetch()
			Swal.fire({
				title: "Cet utilisateur a bien été supprimé.",
				icon: "success",
				position: "bottom-end",
				showConfirmButton: false,
				timer: 1500,
			})
		},

		onError: () => {
			Swal.fire({
				title: "Erreur lors de la suppression.",
				icon: "error",
				position: "bottom-end",
				showConfirmButton: false,
				timer: 1500,
			})
		},
	})

	function handleDeactivateClick(id: GridRowId) {
		Swal.fire({
			title: "Êtes-vous sûr de le désactiver?",
			text: "Cet utilisateur ne pourra plus se connecter !",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Oui, désactiver !",
			cancelButtonText: "Annuler",
		}).then(result => {
			if (result.isConfirmed) {
				postActive.mutate({
					userId: +id,
					activated: false,
				})
			}
		})
	}

	function handleSousAdminClick(id: GridRowId, grant: boolean) {
		Swal.fire({
			title: grant
				? "Accorder les droits sous-admin ?"
				: "Retirer les droits sous-admin ?",
			text: grant
				? "Cet utilisateur pourra accéder au panneau d'administration."
				: "Cet utilisateur perdra l'accès au panneau d'administration.",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: grant ? "Oui, accorder !" : "Oui, retirer !",
			cancelButtonText: "Annuler",
		}).then(result => {
			if (result.isConfirmed) {
				postSousAdmin.mutate({ userId: +id, sousAdmin: grant })
			}
		})
	}

	const handleDeleteClick = async (id: GridRowId) => {
		Swal.fire({
			title: "Êtes-vous sûr?",
			text: "Cet utilisateur n'existera plus !",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Oui, supprimer !",
			cancelButtonText: "Annuler",
		}).then(result => {
			if (result.isConfirmed) {
				deleteUser.mutate(id.toString())
			}
		})
	}

	const onChange = (evt: any) => {
		evt.preventDefault()
		setSearchInput(evt.target.value)
	}

	const onPageChange = (page: number) => {
		if (page > pageNumber) {
			if (!users.isPreviousData) {
				setPageNumber(old => old + 1)
			}
		} else if (page < pageNumber) {
			setPageNumber(old => Math.max(old - 1, 0))
		}
	}

	// const onPageSizeChange = (size: number) => {
	// 	setPageNumber(PAGE)
	// 	setPageSize(size)
	// }
	const onPageSizeChange = (model: GridPaginationModel) => {
		setPageNumber(PAGE)
		setPageSize(model.pageSize)
	}

	return {
		search: searchInput,
		onChange,
		formattedUsers,
		columns,
		users,
		onPageSizeChange,
		sortModel,
		setSortModel,
		onPageChange,
	}
}

export default useUserAdminPage
